import React, { useEffect, useState } from "react";
import { Loader2, Save, Upload, X, QrCode } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, mediaUrl } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const AdminSettings = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)); }, []);

  const uploadLogo = async (file) => {
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo: data.url }));
      toast.success("Logo uploaded — remember to Save");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLogoUploading(false); }
  };

  const uploadQr = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, payment_qr: data.url }));
      toast.success("QR uploaded");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { ...form, delivery_per_kg: parseFloat(form.delivery_per_kg) || 70, delivery_charge: parseFloat(form.delivery_per_kg) || 70, free_delivery_above: 0 });
      toast.success("Settings saved");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  if (!form) return <AdminLayout title="Settings"><div className="flex justify-center py-24"><Loader2 size={36} className="animate-spin text-goldsoft" /></div></AdminLayout>;

  const contactFields = [
    ["contact_phone", "Business Phone", "text"],
    ["whatsapp", "WhatsApp Number", "text"],
    ["contact_email", "Business Email", "text"],
    ["contact_address", "Business Address", "text"],
    ["business_hours", "Business Hours", "text"],
    ["shipping_area", "Shipping Area", "text"],
    ["delivery_days", "Delivery Time", "text"],
    ["couriers", "Courier Services", "text"],
    ["instagram", "Instagram / Social Link", "text"],
    ["delivery_per_kg", "Delivery Charge (₹ per kg)", "number"],
  ];

  return (
    <AdminLayout title="Settings">
      <div className="max-w-2xl space-y-6">
        <div className="bg-white rounded-2xl border border-[#E5DFD3] p-6 sm:p-8 space-y-4">
          <h2 className="font-display text-2xl font-bold text-choco">Branding</h2>
          <p className="text-sm text-muted-foreground">This logo appears across the storefront and admin. Use a square image for best results.</p>
          <div className="flex items-center gap-4">
            <img src={form.logo || ""} alt="Logo" className="h-20 w-20 rounded-full object-cover border border-[#E5DFD3] bg-cream" />
            <label className="inline-flex items-center gap-2 rounded-xl border border-[#E5DFD3] bg-cream px-4 py-2.5 text-sm cursor-pointer hover:border-goldsoft">
              {logoUploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload Logo
              <input data-testid="settings-logo-upload" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} />
            </label>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5DFD3] p-6 sm:p-8 space-y-5">
          <h2 className="font-display text-2xl font-bold text-choco">Store & Contact</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {contactFields.map(([k, label, type]) => (
              <div key={k} className={k === "contact_address" ? "sm:col-span-2" : ""}>
                <label className="text-sm font-medium text-choco">{label}</label>
                <Input data-testid={`settings-${k}`} type={type} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 h-11 bg-cream border-[#E5DFD3]" />
              </div>
            ))}
          </div>
          <div>
            <label className="text-sm font-medium text-choco">About / Brand Story</label>
            <Textarea data-testid="settings-about" value={form.about ?? ""} onChange={(e) => setForm({ ...form, about: e.target.value })} className="mt-1 bg-cream border-[#E5DFD3] min-h-[120px]" />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5DFD3] p-6 sm:p-8 space-y-5">
          <div className="flex items-center gap-2"><QrCode size={22} className="text-goldsoft" /><h2 className="font-display text-2xl font-bold text-choco">Payment (shown to customers)</h2></div>
          <p className="text-sm text-muted-foreground">These details appear on the customer payment page. Customers pay via UPI/QR and enter a reference; you verify each payment under Orders.</p>
          <div>
            <label className="text-sm font-medium text-choco">UPI ID</label>
            <Input data-testid="settings-upi_id" value={form.upi_id ?? ""} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="yourname@upi" className="mt-1 h-11 bg-cream border-[#E5DFD3]" />
          </div>
          <div>
            <label className="text-sm font-medium text-choco">Payment QR Code</label>
            <div className="flex gap-3 items-center mt-1">
              {form.payment_qr ? (
                <div className="relative">
                  <img src={mediaUrl(form.payment_qr)} alt="QR" className="h-20 w-20 rounded-xl object-contain border border-[#E5DFD3] bg-white" />
                  <button onClick={() => setForm({ ...form, payment_qr: "" })} className="absolute -top-2 -right-2 bg-destructive text-white rounded-full h-5 w-5 flex items-center justify-center"><X size={12} /></button>
                </div>
              ) : null}
              <label className="inline-flex items-center gap-2 rounded-xl border border-[#E5DFD3] bg-cream px-4 py-2.5 text-sm cursor-pointer hover:border-goldsoft">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload QR
                <input data-testid="settings-qr-upload" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadQr(e.target.files[0])} />
              </label>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-choco">Payment Instructions</label>
            <Textarea data-testid="settings-payment_instructions" value={form.payment_instructions ?? ""} onChange={(e) => setForm({ ...form, payment_instructions: e.target.value })} className="mt-1 bg-cream border-[#E5DFD3]" />
          </div>
        </div>

        <button data-testid="settings-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save Settings
        </button>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
